import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { BrowserTitleService } from '../../../commonInfra/services/BrowserTitleService';
import { ISanitizer, sanitize } from '../../../commonInfra/utils/Sanitize';
import { ReactDirectiveProps } from '../../../ts/directives/reactDirectiveFactory';
import { IFavoritableItemInfo } from '../../../ts/models/resourceInfoParts';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { ToasterService, ToastType } from '../../../ts/services/ToasterService';
import { setOrderAction } from '../../actions/SetOrderAction';
import { contentCountsUpToDate } from '../../actions/spaces/ContentCountsActions';
import { enterSpace, setSpaceInfoItem } from '../../actions/spaces/EnterSpaceAction';
import { addContentFavorite, removeContentFavorite } from '../../actions/spaces/FavoritesActions';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { updateBreadcrumbs } from '../../actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '../../content/breadcrumbs/BreadcrumbStateInfo';
import { SpaceInfo } from '../../state/SpaceInfo';
import { MapDispatchToProps } from '../../utils/ConnectUtils';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../RouterHelpers/QueryParamUpdateProvider';
import { RouterError } from '../RouterHelpers/RouterError';
import { ConnectedSpaceInfoProvider } from '../RouterHelpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { collectionOwnerCache } from './CollectionOwnersCache';
import { CollectionsResourceQuery } from './Collections';
import { CollectionSpaceOwnProps, ConnectedCollectionSpace } from './CollectionSpace';
import { ICollectionInfo } from './ICollectionInfo';

export interface CollectionSpaceProviderOwnProps extends ReactDirectiveProps {
  readonly siteName: string;
  readonly collectionLuid: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls;
  readonly renderSpace: (props: CollectionSpaceOwnProps) => JSX.Element;
}

export interface CollectionSpaceProviderStateProps {}

export interface CollectionSpaceProviderDispatchProps {
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
  readonly addContentFavorite: (item: IFavoritableItemInfo) => void;
  readonly removeContentFavorite: (item: IFavoritableItemInfo) => void;
}

interface CollectionSpaceProviderPropsFromAngular {
  readonly sanitizer: ISanitizer;
}

export interface LocationProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface CollectionSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly collectionInfo: ICollectionInfo;
}

const InitialState: CollectionSpaceProviderState = {
  initialRender: true,
  renderChildren: false,
  hasError: false,
  collectionInfo: undefined,
};

export type CollectionSpaceProviderNonAngularProps = CollectionSpaceProviderOwnProps & CollectionSpaceProviderStateProps & CollectionSpaceProviderDispatchProps;

export type CollectionSpaceProviderContainerProps = CollectionSpaceProviderNonAngularProps & CollectionSpaceProviderPropsFromAngular;

export class CollectionSpaceProviderContainer extends React.PureComponent<CollectionSpaceProviderContainerProps, CollectionSpaceProviderState> {

  private unmounted: boolean = false;

  public constructor(props: CollectionSpaceProviderContainerProps) {
    super(props);
    this.state = InitialState;
  }

  public render() {
    if (this.state.renderChildren && this.haveAllDataNeededToRender()) {
      const props = {
        angularContext: this.props.angularContext,
        urls: this.props.urls,
        reloadSpace: this.reloadSpace,
        sanitizer: this.props.sanitizer,
        addToFavorites: this.favorite,
        removeFromFavorites: this.unfavorite
      };
      return this.props.renderSpace(props);
    } else if (this.state.hasError) {
      return <RouterError errorType={'collection'} urls={this.props.urls} />;
    } else {
      return null;
    }
  }

  public componentDidMount() {
    BrowserTitleService.setTitleWithPlaceEntityName({ placeEntityName: Messages.collections() });
    this.getData();
  }

  public componentDidUpdate(prevProps: CollectionSpaceProviderContainerProps) {
    if (this.changedCollection(prevProps)) {
      this.updateState(InitialState /* initial state for new collection */);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.props.enterSpace (
        {
          item: this.state.collectionInfo,
          spaceType: 'collection',
          subspaceName: undefined
        }
      );
      this.props.contentCountsUpToDate();
      this.props.requestSpaceContents();

      BrowserTitleService.setTitleWithSpaceEntityNameAndTranslatedSubspaceName({
        spaceEntityName: this.state.collectionInfo.name,
        translatedSubspaceName: Messages.collection()
      });

      this.setState({initialRender: false, renderChildren: true});
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private changedCollection(prevProps: CollectionSpaceProviderContainerProps) {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.collectionLuid) &&
      (prevProps.siteName !== this.props.siteName || prevProps.collectionLuid !== this.props.collectionLuid);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.collectionInfo);
  }

  private getData = async () => {
    try {
      const collectionResult = await new CollectionsResourceQuery().getByLuid(this.props.collectionLuid);
      if (!collectionResult || _.isUndefined(collectionResult.collection)) {
        this.updateState({hasError: true});
      } else {
        this.updateState({collectionInfo: collectionResult.collection});
        const toasterService = ToasterService.getInstance();
        if (collectionResult.errors?.length > 0) {
          toasterService.pop({
            type: ToastType.Warning,
            text: Messages.unableToFetchAllCollectionInfo(),
          });
        }
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private updateState<K extends keyof CollectionSpaceProviderState>(newState: Pick<CollectionSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private reloadSpace = async () => {
    await this.getData();
    this.props.setSpaceInfoItem({
      item: this.state.collectionInfo,
      spaceType: 'collection',
      subspaceName: undefined
    });
    collectionOwnerCache.updateCollectionOwners([this.state.collectionInfo], 'collection');
  }

  private favorite = () => {
    this.props.addContentFavorite(this.state.collectionInfo);
  }

  private unfavorite = () => {
    this.props.removeContentFavorite(this.state.collectionInfo);
  }
}

export namespace CollectionSpaceProviderAngularProvider {
  function getAngularServicesWithServiceLocator() {
    return {
      sanitizer: sanitize
    };
  }

  function selectPropsFromAngularServices(angularServices: CollectionSpaceProviderPropsFromAngular) {
    return angularServices;
  }

  export const HOC = angularProviderHOC<CollectionSpaceProviderNonAngularProps, CollectionSpaceProviderPropsFromAngular, CollectionSpaceProviderPropsFromAngular>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}

const renderSpace = (props: CollectionSpaceOwnProps): JSX.Element => <ConnectedCollectionSpace {...props} />;

const CollectionSpaceProviderAng: React.ComponentType<CollectionSpaceProviderNonAngularProps> = CollectionSpaceProviderAngularProvider.HOC(CollectionSpaceProviderContainer);

const mapDispatchToProps: MapDispatchToProps<CollectionSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  setSpaceInfoItem,
  requestSpaceContents,
  enterSpace,
  contentCountsUpToDate,
  setOrderAction,
  updateBreadcrumbs,
  addContentFavorite,
  removeContentFavorite,
}, dispatch);

export const ConnectedCollectionSpaceProviderContainer: React.ComponentClass<CollectionSpaceProviderOwnProps>
  = connect(undefined, mapDispatchToProps)(CollectionSpaceProviderAng);

export type CollectionSpaceProviderProps = LocationProps & Omit<CollectionSpaceProviderOwnProps, 'renderSpace'>;

export function CollectionSpaceProvider(props: CollectionSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: false,
    shouldUpdateContentTypes: true,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'collection',
    subspaceName: undefined
  };

  const spaceProps = {
    siteName: props.siteName,
    collectionLuid: props.collectionLuid,
    routeLocationSearch: props.routeLocationSearch,
    angularContext: props.angularContext,
    urls: props.urls,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedCollectionSpaceProviderContainer {...spaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}
